import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MailCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [code, setCode] = useState("");

  const doSignup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setDevCode(data.dev_code);
      setStep(2);
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err, "Could not create the account."));
    } finally { setBusy(false); }
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify", { email: form.email, code });
      toast.success("Email verified. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err, "Verification failed."));
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Create your account" : "Verify your email"}
      subtitle={step === 1
        ? "New accounts start with limited access. An administrator will assign your role."
        : `We sent a 6 digit code to ${form.email}.`}
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link></>}
    >
      {step === 1 ? (
        <form onSubmit={doSignup} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" required value={form.name} data-testid="signup-name" className="h-11"
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="su-email">Work email</Label>
            <Input id="su-email" type="email" required value={form.email} data-testid="signup-email" className="h-11"
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <Input id="phone" value={form.phone} className="h-11"
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 00000 00000" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="su-pass">Password</Label>
            <Input id="su-pass" type="password" required minLength={8} value={form.password} data-testid="signup-password" className="h-11"
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" />
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="signup-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Create account
          </Button>
        </form>
      ) : (
        <form onSubmit={doVerify} className="space-y-6">
          {devCode && (
            <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning-soft p-3">
              <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p className="text-xs leading-relaxed text-foreground">
                Email delivery is not configured yet, so here is your code: <b className="num tracking-widest">{devCode}</b>.
                An administrator can add SMTP details in Admin Settings.
              </p>
            </div>
          )}
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="verify-otp">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-base" />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" size="lg" className="w-full" disabled={busy || code.length < 6} data-testid="verify-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Verify email
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
